// Custom Fields: Werte lesen, anzeigen und prüfen (Lead-, Kontakt- und Custom-Activity-Felder).
import type { CustomActivityTypFeld, CustomField, FeldWert } from "../api";
import { htmlZuText, istHtml } from "./html";
import { berlinTeile, datumText, gueltig, zeitpunktText } from "./zeit";

/** Gemeinsame Form der Felddefinitionen (Lead/Kontakt-Feld oder Feld eines Custom-Activity-Typs). */
export type FeldDef = Pick<CustomField, "id" | "name" | "accepts_multiple_values" | "choices" | "description"> & {
  type: string;
  required?: boolean;
  editable_with_roles?: string[] | null;
};

export interface FeldKontext {
  nutzerName: (id: string) => string;
  kontaktName: (id: string) => string;
}

export function istLeer(w: FeldWert | undefined | unknown): boolean {
  return w === null || w === undefined || w === "" || (Array.isArray(w) && w.length === 0);
}

/** Wert eines Custom Fields aus einem Close-Objekt („custom.cf_…“ oder „cf_…“). */
export function customWert(objekt: object | null | undefined, feldId: string): FeldWert | undefined {
  if (!objekt) return undefined;
  const o = objekt as Record<string, unknown>;
  const schluessel = feldId.startsWith("custom.") ? feldId : `custom.${feldId}`;
  if (schluessel in o) return o[schluessel] as FeldWert;
  const roh = feldId.replace(/^custom\./, "");
  if (roh in o) return o[roh] as FeldWert;
  const custom = o.custom as Record<string, FeldWert> | undefined;
  if (custom && roh in custom) return custom[roh];
  return undefined;
}

export function alsListe(w: FeldWert | undefined): string[] {
  if (istLeer(w)) return [];
  return Array.isArray(w) ? w.map(String) : [String(w)];
}

const ZAHL = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

/** Anzeige eines Feldwerts als Text. */
export function feldText(def: Pick<FeldDef, "type"> | undefined, w: FeldWert | undefined, ctx: FeldKontext): string {
  if (istLeer(w)) return "";
  const typ = def?.type ?? "text";
  const liste = alsListe(w);
  switch (typ) {
    case "date":
      return liste.map((x) => datumText(x.slice(0, 10))).join(", ");
    case "datetime":
      return liste.map((x) => (x.length > 10 && gueltig(x) ? zeitpunktText(x) : datumText(x))).join(", ");
    case "number":
      return liste.map((x) => (Number.isFinite(Number(x)) ? ZAHL.format(Number(x)) : x)).join(", ");
    case "user":
      return liste.map((x) => ctx.nutzerName(x)).join(", ");
    case "contact":
      return liste.map((x) => ctx.kontaktName(x)).join(", ");
    case "textarea":
    case "richtextarea":
      return liste.map((x) => (istHtml(x) ? htmlZuText(x) : x)).join(", ");
    default:
      return liste.join(", ");
  }
}

/** Zahl aus deutscher oder englischer Eingabe. */
export function zahlLesen(text: string): number | null {
  const t = text.trim().replace(/\s/g, "");
  if (!t) return null;
  const normal = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
  const n = Number(normal);
  return Number.isFinite(n) ? n : NaN;
}

/** Fehlertext für einen Feldwert oder null (Pflicht, Zahl, Datum+Uhrzeit vollständig). */
export function feldFehler(def: FeldDef, w: FeldWert | undefined, pflicht: boolean): string | null {
  if (istLeer(w)) return pflicht ? "Pflichtfeld" : null;
  if (def.type === "number" && typeof w === "number" && Number.isNaN(w)) return "Bitte eine Zahl eingeben";
  if (def.type === "datetime" && typeof w === "string" && w.length <= 10) return "Bitte auch die Uhrzeit wählen";
  return null;
}

/** Zeitpunkt-Wert für Eingabefelder zerlegen. */
export function zeitpunktTeile(w: FeldWert | undefined): { datum: string; zeit: string } {
  if (typeof w !== "string" || !w) return { datum: "", zeit: "" };
  if (w.length <= 10) return { datum: w, zeit: "" };
  return gueltig(w) ? berlinTeile(w) : { datum: "", zeit: "" };
}

/** Darf die Rolle das Feld ändern? (editable_with_roles leer = alle) */
export function darfAendern(def: Pick<FeldDef, "editable_with_roles">, rolle: string): boolean {
  const rollen = def.editable_with_roles;
  return !rollen || rollen.length === 0 || rollen.includes(rolle);
}

export function istTextFeld(typ: string): boolean {
  return typ === "text" || typ === "textarea" || typ === "richtextarea";
}

export type TypFeld = CustomActivityTypFeld;
